import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { enrichedTaskApiToEnrichedEntity, taskApiStatusToTaskStatus, taskApiToTaskEntity, taskSourceToTaskSourceApi, TaskStatus, taskStatusAndMessageToApiStatusAndDetails, taskStatusToTaskApiStatus } from '../../entities/Task';
import { pagingMetadataToCursorResponse } from '../../utils/PagingUtils';
import { BaseClient } from '../BaseClient';
export class TasksClientBase extends BaseClient {
  constructor() {
    super(...arguments);
    _defineProperty(this, "getTaskImpl", void 0);
    _defineProperty(this, "queryTasksImpl", void 0);
    _defineProperty(this, "searchTasksImpl", void 0);
    _defineProperty(this, "createTaskImpl", void 0);
    _defineProperty(this, "updateTaskImpl", void 0);
    _defineProperty(this, "deleteTaskImpl", void 0);
    _defineProperty(this, "countTaskByStatusImpl", void 0);
    _defineProperty(this, "getDeletedTaskImpl", void 0);
    _defineProperty(this, "getEnrichedTaskImpl", void 0);
    _defineProperty(this, "queryEnrichedTasksImpl", void 0);
  }
  async getDeletedTask(taskId) {
    try {
      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }
      const {
        task
      } = await this.request(this.getDeletedTaskImpl({
        taskId
      }), ...args);
      return this.convertToEntity(task);
    } catch (error) {
      return null;
    }
  }
  async getTask(taskId) {
    try {
      for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        args[_key2 - 1] = arguments[_key2];
      }
      const {
        task
      } = await this.request(this.getTaskImpl({
        taskId
      }), ...args);
      return this.convertToEntity(task);
    } catch (error) {
      this.logError('getTask', 'Failed to get task', {
        taskId
      }, error);
      throw error;
    }
  }
  async queryTasks(query) {
    try {
      for (var _len3 = arguments.length, args = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
        args[_key3 - 1] = arguments[_key3];
      }
      const {
        tasks,
        pagingMetadata
      } = await this.request(this.queryTasksImpl({
        query
      }), ...args);
      if (!tasks) {
        throw new Error('tasks is not an array');
      }
      return {
        items: tasks.map(task => this.convertToEntity(task)),
        ...pagingMetadataToCursorResponse(pagingMetadata)
      };
    } catch (error) {
      this.logError('queryTasks', 'Failed to query tasks', {
        query
      }, error);
      throw error;
    }
  }
  async searchTasks(search) {
    try {
      for (var _len4 = arguments.length, args = new Array(_len4 > 1 ? _len4 - 1 : 0), _key4 = 1; _key4 < _len4; _key4++) {
        args[_key4 - 1] = arguments[_key4];
      }
      const {
        tasks,
        pagingMetadata
      } = await this.request(this.searchTasksImpl({
        search
      }), ...args);
      if (!tasks) {
        throw new Error('tasks is not an array');
      }
      return {
        items: tasks.map(task => this.convertToEntity(task)),
        ...pagingMetadataToCursorResponse(pagingMetadata)
      };
    } catch (error) {
      this.logError('searchTasks', 'Failed to search tasks', {
        search
      }, error);
      throw error;
    }
  }
  async createTask(data) {
    try {
      for (var _len5 = arguments.length, args = new Array(_len5 > 1 ? _len5 - 1 : 0), _key5 = 1; _key5 < _len5; _key5++) {
        args[_key5 - 1] = arguments[_key5];
      }
      const {
        task
      } = await this.request(this.createTaskImpl({
        task: {
          ...data,
          source: taskSourceToTaskSourceApi(data.source),
          status: taskStatusToTaskApiStatus(data.status)
        }
      }), ...args);
      return this.convertToEntity(task);
    } catch (error) {
      this.logError('createTask', 'Failed to create task', {
        data
      }, error);
      throw error;
    }
  }
  async updateTaskGuidelines(task) {
    try {
      this.logInfo('updateTaskGuidelines', 'calling updateTask', {
        id: task.id,
        revision: task.revision,
        guidelines: task.guidelines
      });
      for (var _len6 = arguments.length, args = new Array(_len6 > 1 ? _len6 - 1 : 0), _key6 = 1; _key6 < _len6; _key6++) {
        args[_key6 - 1] = arguments[_key6];
      }
      const response = await this.request(this.updateTaskImpl({
        task: {
          id: task.id,
          revision: task.revision,
          guidelines: task.guidelines
        },
        fieldMask: ['guidelines']
      }), ...args);
      return this.convertToEntity(response.task);
    } catch (error) {
      this.logError('updateTaskGuidelines', 'Failed to update task guidelines', {
        task
      }, error);
      throw error;
    }
  }
  async updateTaskStatus(task) {
    try {
      const {
        status: apiStatus,
        actionNeededOptions,
        awaitingDetailsOptions,
        fieldMask
      } = taskStatusAndMessageToApiStatusAndDetails(task.status, task.statusMessage);
      this.logInfo('updateTaskStatus', 'calling updateTask', {
        id: task.id,
        revision: task.revision,
        status: apiStatus,
        actionNeededOptions,
        awaitingDetailsOptions,
        fieldMask
      });
      for (var _len7 = arguments.length, args = new Array(_len7 > 1 ? _len7 - 1 : 0), _key7 = 1; _key7 < _len7; _key7++) {
        args[_key7 - 1] = arguments[_key7];
      }
      const response = await this.request(this.updateTaskImpl({
        task: {
          id: task.id,
          revision: task.revision,
          status: apiStatus,
          actionNeededOptions,
          awaitingDetailsOptions
        },
        fieldMask
      }), ...args);
      return this.convertToEntity(response.task);
    } catch (error) {
      this.logError('updateTaskStatus', 'Failed to update task status', {
        task
      }, error);
      throw error;
    }
  }
  async deleteTask(taskId) {
    try {
      for (var _len8 = arguments.length, args = new Array(_len8 > 1 ? _len8 - 1 : 0), _key8 = 1; _key8 < _len8; _key8++) {
        args[_key8 - 1] = arguments[_key8];
      }
      await this.request(this.deleteTaskImpl({
        taskId
      }), ...args);
    } catch (error) {
      this.logError('deleteTask', 'Failed to delete task', {
        taskId
      }, error);
      throw error;
    }
  }
  async countTaskByStatus() {
    try {
      for (var _len9 = arguments.length, args = new Array(_len9), _key9 = 0; _key9 < _len9; _key9++) {
        args[_key9] = arguments[_key9];
      }
      const {
        statusesCounts
      } = await this.request(this.countTaskByStatusImpl({}), ...args);
      if (!statusesCounts) {
        throw new Error('statusesCounts is not an array');
      }
      const initialCounts = Object.values(TaskStatus).reduce((acc, status) => ({
        ...acc,
        [status]: 0
      }), {});
      return statusesCounts.reduce((acc, _ref) => {
        let {
          status,
          count
        } = _ref;
        return {
          ...acc,
          [taskApiStatusToTaskStatus(status)]: count
        };
      }, initialCounts);
    } catch (error) {
      this.logError('countTaskByStatus', 'Failed to count tasks by status', {}, error);
      throw error;
    }
  }
  async getEnrichedTask(enrichedTaskId) {
    try {
      for (var _len0 = arguments.length, args = new Array(_len0 > 1 ? _len0 - 1 : 0), _key0 = 1; _key0 < _len0; _key0++) {
        args[_key0 - 1] = arguments[_key0];
      }
      const {
        enrichedTask
      } = await this.request(this.getEnrichedTaskImpl({
        enrichedTaskId
      }), ...args);
      return enrichedTaskApiToEnrichedEntity(enrichedTask);
    } catch (error) {
      this.logError('getEnrichedTask', 'Failed to get enriched task', {
        enrichedTaskId
      }, error);
      throw error;
    }
  }
  async queryEnrichedTasks(query) {
    try {
      for (var _len1 = arguments.length, args = new Array(_len1 > 1 ? _len1 - 1 : 0), _key1 = 1; _key1 < _len1; _key1++) {
        args[_key1 - 1] = arguments[_key1];
      }
      const {
        enrichedTasks,
        pagingMetadata
      } = await this.request(this.queryEnrichedTasksImpl({
        query
      }), ...args);
      if (!enrichedTasks) {
        throw new Error('enrichedTasks is not an array');
      }
      return {
        items: enrichedTasks.map(enrichedTaskApiToEnrichedEntity),
        ...pagingMetadataToCursorResponse(pagingMetadata)
      };
    } catch (error) {
      this.logError('queryEnrichedTasks', 'Failed to query enriched tasks', {
        query
      }, error);
      throw error;
    }
  }
  convertToEntity(task) {
    try {
      return taskApiToTaskEntity(task);
    } catch (error) {
      this.logError('convertToEntity', 'Failed to convert task', {
        task
      }, error);
      throw error;
    }
  }
  getServiceNameForLogs() {
    return 'TasksClient';
  }
}
//# sourceMappingURL=TasksClientBase.js.map